(() => {
  "use strict";

  function formatMoney(value) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value || 0) / 100);
  }

  function getPanel() {
    return document.querySelector("[data-ab-pdp]");
  }

  function getNativeArea(panel) {
    return (
      panel?.parentElement?.querySelector(".ab-pp-native") ||
      document.querySelector(".ab-pp-native")
    );
  }

  function getVariantData(panel) {
    const script = panel?.querySelector("[data-ab-pdp-variants]");
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || "[]");
    } catch (error) {
      console.error("The Appliance Barn: invalid variant data.", error);
      return [];
    }
  }

  function findVariant(panel, variantId) {
    return getVariantData(panel).find(
      variant => String(variant.id) === String(variantId)
    ) || null;
  }

  function setButtonText(button, text) {
    if (!button) return;

    const textSpan = [...button.querySelectorAll("span")].find(
      element => element.children.length === 0 && element.textContent.trim()
    );

    if (textSpan) {
      textSpan.textContent = text;
      return;
    }

    const textNode = [...button.childNodes].find(
      node => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );

    if (textNode) {
      textNode.textContent = ` ${text} `;
      return;
    }

    button.textContent = text;
  }

  function updatePanelData(panel, variant) {
    panel.dataset.variantId = String(variant.id || "");
    panel.dataset.price = String(variant.price || 0);
    panel.dataset.sku = variant.sku || "";

    if (variant.url) {
      panel.dataset.url = variant.url;
    } else if (variant.id) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", String(variant.id));
      panel.dataset.url = url.pathname + url.search;
    }

    if (variant.image) {
      panel.dataset.image = variant.image;
    }
  }

  function updateSku(panel, variant) {
    const element = panel.querySelector(
      "[data-ab-pdp-sku], .ab-pdp__sku"
    );

    if (!element) return;

    element.textContent = variant.sku ? `SKU: ${variant.sku}` : "";
    element.style.display = variant.sku ? "" : "none";
  }

  function updatePrice(panel, variant) {
    const priceElement = panel.querySelector(
      "[data-ab-pdp-price], .ab-pdp__price"
    );

    const priceNote = panel.querySelector(
      "[data-ab-pdp-price-note], .ab-pdp__price-wrap small"
    );

    if (!priceElement) return;

    const priceRequest =
      panel.dataset.priceRequest === "true" ||
      Number(variant.price || 0) === 0;

    if (priceRequest) {
      priceElement.textContent = "Price on request";
      priceElement.classList.add("ab-pdp__price--call");
      if (priceNote) {
        priceNote.textContent = "Contact us for our latest price";
      }
      return;
    }

    priceElement.textContent =
      variant.priceFormatted || formatMoney(variant.price);
    priceElement.classList.remove("ab-pdp__price--call");

    if (priceNote) {
      priceNote.textContent = "Price includes VAT";
    }
  }

  function updateSaving(panel, variant) {
    const price = Number(variant.price || 0);
    const compareAtPrice = Number(
      variant.compareAtPrice ?? variant.compare_at_price ?? 0
    );

    const hasSaving =
      panel.dataset.priceRequest !== "true" &&
      price > 0 &&
      compareAtPrice > price;

    let element = panel.querySelector(
      "[data-ab-pdp-saving], .ab-pdp__saving"
    );

    if (!element) {
      const priceLine = panel.querySelector(".ab-pdp__price-line");
      if (!priceLine) return;

      element = document.createElement("div");
      element.className = "ab-pdp__saving";
      element.setAttribute("data-ab-pdp-saving", "");
      element.innerHTML = `
        <strong data-ab-pdp-saving-amount></strong>
        <span data-ab-pdp-rrp></span>
      `;

      priceLine.appendChild(element);
    }

    if (!hasSaving) {
      element.style.display = "none";
      return;
    }

    const savingAmount = element.querySelector(
      "[data-ab-pdp-saving-amount]"
    );
    const rrp = element.querySelector("[data-ab-pdp-rrp]");

    if (savingAmount) {
      savingAmount.textContent =
        `SAVE ${variant.savingFormatted || formatMoney(compareAtPrice - price)}`;
    }

    if (rrp) {
      rrp.textContent =
        `RRP ${variant.compareAtPriceFormatted || formatMoney(compareAtPrice)}`;
    }

    element.style.display = "";
  }

  function updateStock(panel, variant) {
    const dot = panel.querySelector("[data-ab-pdp-stock-dot]");
    const status = panel.querySelector("[data-ab-pdp-stock-status]");

    if (dot) {
      dot.classList.toggle("is-order", !variant.inStock);
    }

    if (status) {
      status.textContent = variant.inStock
        ? "IN STOCK"
        : "AVAILABLE TO ORDER";
    }
  }

  function updatePurchaseButton(panel, variant) {
    const nativeArea = getNativeArea(panel);
    if (!nativeArea) return;

    const button = nativeArea.querySelector(".add-to-cart-button");
    if (!button) return;

    const priceRequest =
      panel.dataset.priceRequest === "true" ||
      Number(variant.price || 0) === 0;

    if (priceRequest) {
      setButtonText(button, "Request Your Price");
      button.classList.add("ab-pdp__request-price");
      button.dataset.priceRequest = "true";
      button.type = "button";
    } else {
      setButtonText(
        button,
        `Add to Basket — ${variant.priceFormatted || formatMoney(variant.price)}`
      );
      button.classList.remove("ab-pdp__request-price");
      delete button.dataset.priceRequest;
      button.type = "submit";
    }

    nativeArea.querySelectorAll(".shopify-payment-button").forEach(
      element => {
        element.style.display = priceRequest ? "none" : "";
      }
    );
  }

  function updateUrl(variant) {
    if (!variant?.id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", String(variant.id));
    window.history.replaceState({}, "", url);
  }

  function updateEverything(variantId, eventVariant = null) {
    const panel = getPanel();
    if (!panel) return;

    let variant = findVariant(panel, variantId);

    if (!variant && eventVariant) {
      const price = Number(eventVariant.price || 0);
      const compareAtPrice = Number(eventVariant.compare_at_price || 0);

      variant = {
        ...eventVariant,
        id: eventVariant.id,
        price,
        priceFormatted: formatMoney(price),
        compareAtPrice,
        compareAtPriceFormatted: formatMoney(compareAtPrice),
        savingFormatted: formatMoney(
          Math.max(0, compareAtPrice - price)
        ),
        sku: eventVariant.sku || "",
        image:
          eventVariant.featured_image?.src ||
          eventVariant.featured_media?.preview_image?.src ||
          "",
        inStock: eventVariant.available !== false
      };
    }

    if (!variant) return;

    updatePanelData(panel, variant);
    updateSku(panel, variant);
    updatePrice(panel, variant);
    updateSaving(panel, variant);
    updateStock(panel, variant);
    updatePurchaseButton(panel, variant);
    updateUrl(variant);
  }

  function handleVariantEvent(event) {
    const variant = event.detail?.variant;
    if (!variant?.id) return;

    const panel = getPanel();
    if (!panel || !findVariant(panel, variant.id)) return;

    window.requestAnimationFrame(() => {
      updateEverything(variant.id, variant);
    });
  }

  document.addEventListener("variant:change", handleVariantEvent);
  document.addEventListener("variant:update", handleVariantEvent);

  document.addEventListener("change", event => {
    const target = event.target;

    if (
      !(target instanceof HTMLElement) ||
      !target.matches('form[action*="/cart/add"] input[name="id"]')
    ) {
      return;
    }

    const panel = getPanel();
    if (!panel || !target.value) return;

    const variant = findVariant(panel, target.value);
    if (!variant) return;

    window.requestAnimationFrame(() => {
      updateEverything(variant.id, variant);
    });
  }, true);

  document.addEventListener("click", event => {
    const button = event.target.closest?.(
      '.add-to-cart-button[data-price-request="true"]'
    );

    if (!button) return;

    const panel = getPanel();
    if (!panel) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const quote = window.MultiBuyQuote;

    if (!quote || typeof quote.add !== "function") {
      window.alert(
        "Please call 01843 610025 for your price, or refresh this page and try again."
      );
      return;
    }

    const data = panel.dataset;
    if (!data.variantId) return;

    const existing = typeof quote.read === "function"
      ? quote.read().find(
          item =>
            String(item.variantId) === String(data.variantId) &&
            item.priceRequest === true
        )
      : null;

    if (existing) {
      if (window.MultiBuyQuoteDrawer?.open) {
        window.MultiBuyQuoteDrawer.open();
      } else {
        window.location.assign("/pages/multi-buy-quote");
      }
      return;
    }

    const variant = findVariant(panel, data.variantId);
    const variantTitle = variant?.title || "";

    quote.add({
      variantId: String(data.variantId),
      productId: String(data.productId || ""),
      title: data.title || "",
      variantTitle: variantTitle === "Default Title" ? "" : variantTitle,
      sku: data.sku || "",
      price: 0,
      url: data.url || window.location.pathname,
      image: data.image || "",
      quantity: 1,
      priceRequest: true
    });

    if (!window.MultiBuyQuoteDrawer?.open) {
      window.location.assign("/pages/multi-buy-quote");
    }
  }, true);

  let lastVariantId = null;

  function checkUrlVariant() {
    const panel = getPanel();
    if (!panel) return;

    const variantId = new URL(window.location.href).searchParams.get(
      "variant"
    );

    if (!variantId || String(variantId) === String(lastVariantId)) {
      return;
    }

    const variant = findVariant(panel, variantId);
    if (!variant) return;

    lastVariantId = String(variantId);

    if (String(panel.dataset.variantId) !== String(variantId)) {
      updateEverything(variantId, variant);
    }
  }

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function (...args) {
    const result = originalPushState.apply(this, args);
    window.requestAnimationFrame(checkUrlVariant);
    return result;
  };

  window.history.replaceState = function (...args) {
    const result = originalReplaceState.apply(this, args);
    window.requestAnimationFrame(checkUrlVariant);
    return result;
  };

  window.addEventListener("popstate", checkUrlVariant);

  function initialise() {
    const panel = getPanel();
    if (!panel) return;

    const variantId =
      new URL(window.location.href).searchParams.get("variant") ||
      panel.dataset.variantId;

    if (!variantId) return;

    const variant = findVariant(panel, variantId);
    if (!variant) return;

    lastVariantId = String(variantId);
    updateEverything(variantId, variant);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialise, {
      once: true
    });
  } else {
    initialise();
  }
})();